import React, { useState, useEffect } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Grid, TextField, Autocomplete, Chip, Box } from '@mui/material';
import axios from 'axios';

const GovernmentSelectors = ({ formData, setFormData }) => {
    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [crops, setCrops] = useState([]);
    const seasons = ['Kharif', 'Rabi', 'Zaid', 'All'];
    const [cropSeasonsMap, setCropSeasonsMap] = useState({});

    // Fetch initial data
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [statesRes, cropsRes, mapRes] = await Promise.all([
                    axios.get('/api/government/states'),
                    axios.get('/api/government/crops'),
                    axios.get('/api/government/crop-seasons-map')
                ]);
                setStates(statesRes.data);
                setCrops(cropsRes.data);
                setCropSeasonsMap(mapRes.data);
            } catch (err) {
                console.error('Failed to fetch initial government data:', err);
            }
        };
        fetchInitialData();
    }, []);

    // Auto-select season based on crops and date
    useEffect(() => {
        if (!formData.crop_name) return;

        const selectedCrops = formData.crop_name.split(', ').filter(c => c);
        if (selectedCrops.length === 0) return;

        // Get common seasons for selected crops
        let possibleSeasons = new Set(['Kharif', 'Rabi', 'Zaid']);
        selectedCrops.forEach(crop => {
            const cropSeasons = cropSeasonsMap[crop] || [];
            if (cropSeasons.length > 0) {
                const currentSet = new Set(['Kharif', 'Rabi', 'Zaid']);
                currentSet.forEach(s => {
                    if (!cropSeasons.includes(s)) possibleSeasons.delete(s);
                });
            }
        });

        const availableSeasons = Array.from(possibleSeasons);

        if (availableSeasons.length === 1) {
            if (formData.crop_season !== availableSeasons[0]) {
                setFormData(prev => ({ ...prev, crop_season: availableSeasons[0] }));
            }
        } else if (availableSeasons.length > 1) {
            // Pick based on current month
            const month = new Date().getMonth(); // 0-11
            let suggested = '';

            if (month >= 5 && month <= 9) suggested = 'Kharif'; // Jun-Oct
            else if (month >= 10 || month <= 2) suggested = 'Rabi'; // Nov-Mar
            else suggested = 'Zaid'; // Mar-Jun

            if (availableSeasons.includes(suggested)) {
                if (formData.crop_season !== suggested) {
                    setFormData(prev => ({ ...prev, crop_season: suggested }));
                }
            }
        }
    }, [formData.crop_name, cropSeasonsMap, setFormData, formData.crop_season]);

    const handleStateChange = async (e) => {
        const state = e.target.value;
        setFormData(prev => ({ ...prev, state, district: '' }));
        if (state) {
            try {
                const response = await axios.get(`/api/government/districts/${state}`);
                setDistricts(response.data);
            } catch (error) {
                console.error('Error fetching districts:', error);
            }
        } else {
            setDistricts([]);
        }
    };

    const handleDistrictChange = (e) => {
        setFormData(prev => ({ ...prev, district: e.target.value }));
    };

    const handleSeasonChange = (e) => {
        setFormData(prev => ({ ...prev, crop_season: e.target.value }));
    };

    // Style for the dropdown menu paper to ensure opacity and proper layering
    const menuProps = {
        PaperProps: {
            sx: {
                backgroundColor: '#1a1a1a', // Solid dark background
                border: '1px solid rgba(76, 175, 80, 0.4)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
                '& .MuiMenuItem-root': {
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                        fontWeight: 'bold',
                        color: '#4CAF50',
                        '&:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.3)',
                        }
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    }
                },
                zIndex: 1500,
            }
        },
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                    <InputLabel id="state-label" sx={{ color: 'rgba(76, 175, 80, 0.8)' }}>State</InputLabel>
                    <Select
                        labelId="state-label"
                        label="State"
                        name="state"
                        value={formData.state || ''}
                        onChange={handleStateChange}
                        MenuProps={menuProps}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 175, 80, 0.3)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4CAF50' },
                        }}
                    >
                        {states.map(state => (
                            <MenuItem key={state} value={state}>{state}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required disabled={!formData.state}>
                    <InputLabel id="district-label" sx={{ color: 'rgba(76, 175, 80, 0.8)' }}>District</InputLabel>
                    <Select
                        labelId="district-label"
                        label="District"
                        name="district"
                        value={formData.district || ''}
                        onChange={handleDistrictChange}
                        MenuProps={menuProps}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 175, 80, 0.3)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4CAF50' },
                        }}
                    >
                        {districts.map(district => (
                            <MenuItem key={district} value={district}>{district}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <TextField
                    fullWidth
                    required
                    label="Village"
                    name="village"
                    value={formData.village || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, village: e.target.value }))}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 175, 80, 0.3)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4CAF50' },
                        '& .MuiInputLabel-root': { color: 'rgba(76, 175, 80, 0.8)' }
                    }}
                />
            </Grid>

            <Grid item xs={12} sm={6}>
                <Autocomplete
                    multiple
                    limitTags={2}
                    options={crops}
                    value={formData.crop_name ? formData.crop_name.split(', ').filter(c => c) : []}
                    onChange={(event, newValue) => {
                        if (newValue.length <= 5) {
                            setFormData(prev => ({ ...prev, crop_name: newValue.join(', ') }));
                        }
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                                <Chip
                                    key={key}
                                    label={option}
                                    {...tagProps}
                                    size="small"
                                    sx={{
                                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                        color: '#4CAF50',
                                        borderColor: 'rgba(76, 175, 80, 0.5)'
                                    }}
                                />
                            );
                        })
                    }
                    PaperComponent={({ children }) => (
                        <Box sx={{
                            backgroundColor: '#1a1a1a',
                            border: '1px solid rgba(76, 175, 80, 0.4)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
                            color: '#fff',
                            borderRadius: '4px'
                        }}>
                            {children}
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Crop Name (Select up to 5)"
                            required={!formData.crop_name}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 175, 80, 0.3)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4CAF50' },
                                '& .MuiInputLabel-root': { color: 'rgba(76, 175, 80, 0.8)' }
                            }}
                        />
                    )}
                    sx={{
                        '& .MuiAutocomplete-listbox': {
                            backgroundColor: '#1a1a1a',
                            '& .MuiAutocomplete-option[aria-selected="true"]': {
                                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                color: '#4CAF50',
                            }
                        }
                    }}
                />
            </Grid>

            <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                    <InputLabel id="season-label" sx={{ color: 'rgba(76, 175, 80, 0.8)' }}>Crop Season</InputLabel>
                    <Select
                        labelId="season-label"
                        label="Crop Season"
                        name="crop_season"
                        value={formData.crop_season || ''}
                        onChange={handleSeasonChange}
                        MenuProps={menuProps}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 175, 80, 0.3)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4CAF50' },
                        }}
                    >
                        {seasons.map(season => (
                            <MenuItem key={season} value={season}>{season}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    );
};

export default GovernmentSelectors;
